import React from 'react';
import { toast } from 'react-toastify';

import DisplayTemplate from '../displayTemplate';
import FIBText from './FIBText';
import Button from '../Button';
import HelpText from '../quiz/HelpText';
import { getSpeechConfig } from '../../store/quiz/quizBaseApi';
import quizPlayIcon from "../../assets/images/quiz/speech-play.svg";
import quizPauseIcon from "../../assets/images/quiz/speech-pause.svg";
import quizReplayIcon from "../../assets/images/quiz/speech-retry.svg";
import speechLoadingIcon from "../../assets/images/quiz/speech-loading.gif"
import TextToSpeech from './TextToSpeech';

const iconStyle = { marginRight: '1rem' };
const PlayIcon = () => <img style={iconStyle} src={quizPlayIcon} />;
const PauseIcon = () => <img style={iconStyle} src={quizPauseIcon} />;
const RetryIcon = () => <img style={iconStyle} src={quizReplayIcon} />;
const LoadingIcon = () => <img style={iconStyle} height={'50px'} src={speechLoadingIcon} />;

function TextInner({
    content,
    style,
    enableTextToAudio,
    autoPlay = true,
    onAudioPlayback,
    onAudioPlaybackComplete,
    helpText,
    middle = false,
}) {
    const [audioUrl, setAudioUrl] = React.useState(null);
        const [isLoading, setIsLoading] = React.useState(false);
        const [isPlaying, setIsPlaying] = React.useState(false);
        const student = JSON.parse(localStorage.getItem('profile')) || {};

        const audioRef = React.useRef(null);
        const played = React.useRef(false);

        const togglePlayPause = () => {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
                if (!played.current && onAudioPlayback && typeof onAudioPlayback === 'function') {
                    onAudioPlayback();
                }

                played.current = true;
            }
        };

        const handlePlay = () => {
            setIsPlaying(true);
        };

        const handlePause = () => {
            setIsPlaying(false);
        };

        const handleEnd = () => {
            setIsPlaying(false);

            if (onAudioPlaybackComplete && typeof onAudioPlaybackComplete === 'function') {
                onAudioPlaybackComplete();
            }
        }

        const fetchAudio = async () => {
            if (audioUrl) {}
            setIsLoading(true);

            try {
                let conf = await getSpeechConfig(String(student.geographicalType).toLowerCase(), student.learningGeneration);
                const data = await textToSpeech({ text: content, ...conf });

                if (data && data.response && data.response.audio) {
                    setAudioUrl(data.response.audio);

                    // Cache the response for later re-renders (will get cleared on logout)
                    localStorage.setItem(String(content).trim(), data.response.audio);
                }

            } catch (error) {
                let message = error.message;

                if (error.response) {
                    let { data } = error.response;
                    let { info } = data;

                    message = info.detail;
                }
                toast.error(message);
            } finally {
                setIsLoading(false);
            }
        };

        React.useEffect(() => {
            if (!content) return;

            // Retrive instead of fetching via API call everytime
            let existingAudio = localStorage.getItem(String(content).trim());

            if (existingAudio) {
                return setAudioUrl(existingAudio);
            }

            if (autoPlay) {
                // fetchAudio();
            }
        }, [content]);

        if (enableTextToAudio) {
            return <TextToSpeech
                key={content}
                styles={style}
                autoPlay={true}
                helpText={helpText}
                content={content}
                onAudioPlaybackComplete={onAudioPlaybackComplete}
                middle={middle}
            />
        }

        if (middle) {
            style = {
                textAlign: 'center',
                jutifyContent: 'center',
                alignItems: 'center'
            }
        }

        return (
            <React.Fragment>
                <div style={{ ...styles.container, ...style }}>
                    {enableTextToAudio &&
                        <div style={styles.hearButtonContainer}>
                            <Button endIcon={(!isLoading && !audioUrl) ? <RetryIcon /> : (isPlaying ? <PauseIcon /> : (isLoading ? <LoadingIcon /> : <PlayIcon />))}
                                size={'small'} type={'secondary'} style={{ ...styles.button, }}
                                onClick={!audioUrl ? fetchAudio : togglePlayPause} disabled={isLoading} />
                        </div>
                    }

                    {content && <span style={{ ...styles.text, textAlign: middle ? "center" : "left" }}>{String(content).replace(/\\n/g, '\n')}</span>}

                    {helpText && Object.keys(helpText).length > 0 && <HelpText style={styles.helpText} data={helpText} />}

                </div>
                {enableTextToAudio && <audio
                    ref={audioRef}
                    src={audioUrl}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onEnded={handleEnd}
                />}

            </React.Fragment>
        );
    }

export default function Text({
    content,
    style,
    enableTextToAudio,
    autoPlay = true,
    onAudioPlayback,
    onAudioPlaybackComplete,
    contentType,
    selected,
    disabled,
    handleAnswerSelection,
    renderDefault = true,
    language,
    contentDisplayType,
    contentUrl,
    mediaStyle,
    overrideHTML,
    helpText,
    templateType,
    middle = false,
    ...props
}) {
    const TextComponent = contentDisplayType == 'FIB'
        ? FIBText.bind(null, {
            disabled, value: selected, renderDefault, handleAnswerSelection, length: props.length, content, style
        })
        : () => (
            <TextInner
                content={content}
                style={style}
                enableTextToAudio={enableTextToAudio}
                autoPlay={autoPlay}
                onAudioPlayback={onAudioPlayback}
                onAudioPlaybackComplete={onAudioPlaybackComplete}
                helpText={helpText}
                middle={middle}
            />
        );

    if (renderDefault) {
        if (contentDisplayType == 'FIB') {
            return <TextComponent />;
        }
        return (
            <TextInner
                content={content}
                style={style}
                enableTextToAudio={enableTextToAudio}
                autoPlay={autoPlay}
                onAudioPlayback={onAudioPlayback}
                onAudioPlaybackComplete={onAudioPlaybackComplete}
                helpText={helpText}
                middle={middle}
            />
        );
    }

    return <DisplayTemplate templateType={templateType} components={[TextComponent]} {...props} />;
}

const styles = {
    container: {
        textAlign: 'left',
        display: 'flex',
    },
    button: {
        marginLeft: '10px',
        background: 'transparent',
        fontWeight: 'bold'
    },
    helpText: {

    },
    text: {
        marginTop: 'auto',
        marginBottom: 'auto',
        lineHeight: '25px',
        marginRight: '1rem',
        whiteSpace: 'pre-line' // honour authored line breaks, still wraps
    },
    hearButtonContainer: {
        display: 'flex',
        alignItems: 'start'
    }
}
